import React from 'react'
import { Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import classNames from 'classnames'
import moment from 'moment'
import { ITransaction } from 'models/transaction'
import { IWithdrawalRequest, IWithdrawalSubmission } from 'models/withdrawalRequest'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { getWithdrawalPeriodDays } from 'shared/utils/formatWithdrawalPeriod'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import {
  getWithdrawalRequestStatusColor,
  getWithdrawalRequestStatusLabel,
  getWithdrawalTransactionStatusColor,
  getWithdrawalTransactionStatusLabel,
  withdrawalSubmissionSourceLabels
} from 'shared/constants/withdrawals'
import { OrderModal } from 'components/orders/OrderModal/OrderModal'
import styles from './WithdrawalRequestDetails.module.scss'

interface IWithdrawalRequestDetailsProps {
  request: IWithdrawalRequest
  isLoading?: boolean
  // Продавец — только в списке заявок у суперадмина, в бухгалтерии он и так известен
  seller?: React.ReactNode
}

interface ISaleRow {
  _id: string
  order_id?: string | null
  order_number?: string | number | null
  order_created_at?: string | null
  sum?: number
  status?: ITransaction['status']
  submission?: IWithdrawalSubmission
}

const getValidOrderId = (orderId?: string | null) => {
  return orderId && orderId !== 'undefined' ? orderId : null
}

const getSaleRows = (request: IWithdrawalRequest): ISaleRow[] => {
  const items: Array<string | ITransaction> = request.transactions?.length
    ? request.transactions
    : request.transaction_ids ?? []
  const submissions = request.submissions ?? []

  const rows = items.map((item): ISaleRow => {
    const sale = typeof item === 'string' ? { _id: item, order_number: item } : item
    return {
      ...sale,
      submission: submissions.find(({ transaction_ids }) => transaction_ids?.includes(sale._id))
    }
  })

  return rows.sort((a, b) =>
    moment(b.submission?.submitted_at ?? 0).diff(moment(a.submission?.submitted_at ?? 0))
  )
}

export const renderWithdrawalPeriod = (request: IWithdrawalRequest) => {
  const [start, end] = getWithdrawalPeriodDays(request)

  return end ? <>{start} –<br />{end}</> : start
}

const renderSum = (sum?: number) => (typeof sum === 'number' ? getNormalizedPrice(sum) : '—')

const renderDate = (date: string | Date | null | undefined, format: string) => (
  date ? moment(date).locale('ru').format(format) : '—'
)

const renderStatus = ({ status, submission }: ISaleRow) => (
  <div className={styles.statusCell}>
    {status && (
      <Tag color={getWithdrawalTransactionStatusColor(status)}>
        {getWithdrawalTransactionStatusLabel(status)}
      </Tag>
    )}
    {submission && (
      <Typography.Text type='secondary'>
        {withdrawalSubmissionSourceLabels[submission.source]}
      </Typography.Text>
    )}
  </div>
)

const columns: ColumnsType<ISaleRow> = [
  {
    title: 'Номер заказа',
    dataIndex: 'order_number',
    width: 90,
    render: (_, { order_number }) => <Typography.Text>{order_number ?? '—'}</Typography.Text>
  },
  {
    title: 'Сумма',
    dataIndex: 'sum',
    render: (_, { sum }) => <Typography.Text className={styles.nowrap}>{renderSum(sum)}</Typography.Text>
  },
  {
    title: 'Дата заказа',
    dataIndex: 'order_created_at',
    render: (_, { order_created_at }) => <Typography.Text>{renderDate(order_created_at, 'LL')}</Typography.Text>
  },
  {
    title: 'Дата подачи заявки',
    dataIndex: 'submitted_at',
    render: (_, { submission }) => <Typography.Text>{renderDate(submission?.submitted_at, 'LL')}</Typography.Text>
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    width: 150,
    render: (_, row) => renderStatus(row)
  }
]

const mobileColumns: ColumnsType<ISaleRow> = [
  {
    title: 'Заказ',
    dataIndex: 'order_number',
    width: '55%',
    render: (_, { order_number, order_created_at, submission }) => (
      <div className={styles.mobileCell}>
        <Typography.Text className={styles.mobileNumber}>{order_number ?? '—'}</Typography.Text>
        {order_created_at && (
          <Typography.Text type='secondary' className={styles.mobileSubText}>
            Заказ от {renderDate(order_created_at, 'DD.MM.YYYY')}
          </Typography.Text>
        )}
        <Typography.Text type='secondary' className={styles.mobileSubText}>
          Заявка от {renderDate(submission?.submitted_at, 'DD.MM.YYYY')}
        </Typography.Text>
      </div>
    )
  },
  {
    title: 'Сумма и статус',
    dataIndex: 'sum',
    width: '45%',
    render: (_, row) => (
      <div className={styles.mobileCell}>
        <Typography.Text>{renderSum(row.sum)}</Typography.Text>
        {renderStatus(row)}
      </div>
    )
  }
]

// Содержимое модалки заявки на вывод: шапка, продажи с подачами и заказ по клику на строку
export const WithdrawalRequestDetails: React.FC<IWithdrawalRequestDetailsProps> = ({
  request,
  isLoading,
  seller
}) => {
  const isMobile = useIsMobile()
  const [orderId, setOrderId] = React.useState<string | null>(null)

  return (
    <>
      <Typography.Title level={4}>
        Заявка {request.number ?? request._id}
      </Typography.Title>
      <div className={classNames(styles.head, seller && styles.headWithSeller)}>
        {seller && (
          <div className={styles.headItem}>
            <Typography.Text className={styles.headLabel}>Продавец / магазин</Typography.Text>
            <Typography.Text>{seller}</Typography.Text>
          </div>
        )}
        <div className={styles.headItem}>
          <Typography.Text className={styles.headLabel}>Сумма</Typography.Text>
          <Typography.Text>{getNormalizedPrice(request.sum)}</Typography.Text>
        </div>
        <div className={styles.headItem}>
          <Typography.Text className={styles.headLabel}>Расчётный период</Typography.Text>
          <Typography.Text>{renderWithdrawalPeriod(request)}</Typography.Text>
        </div>
        <div className={styles.headItem}>
          <Typography.Text className={styles.headLabel}>Статус</Typography.Text>
          <Tag color={getWithdrawalRequestStatusColor(request.status)}>
            {getWithdrawalRequestStatusLabel(request.status)}
          </Tag>
        </div>
      </div>
      <Table
        columns={isMobile ? mobileColumns : columns}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        dataSource={getSaleRows(request)}
        loading={isLoading}
        rowKey={(item) => item._id}
        pagination={false}
        scroll={isMobile ? undefined : { x: 760 }}
        onRow={({ order_id }) => {
          const validOrderId = getValidOrderId(order_id)
          return {
            onClick: () => validOrderId && setOrderId(validOrderId),
            style: { cursor: validOrderId ? 'pointer' : 'default' }
          }
        }}
      />
      <OrderModal orderId={orderId} onClose={() => setOrderId(null)} />
    </>
  )
}
